import { PAIRING_RATING_LABELS, type PairingRating } from '../types'

export type RatingFilterOperator = '>=' | '=' | '<='

export type RatingFilterState = {
  // null = no rating filter active
  threshold: PairingRating | null
  operator: RatingFilterOperator
}

export const DEFAULT_RATING_FILTER_STATE: RatingFilterState = {
  threshold: null,
  operator: '>=',
}

/**
 * Pure helper applied at every consumer that filters pair candidates. When
 * threshold is null the filter is a no-op. Pairs without a rating are treated
 * as "no rating" — they pass `>=` / `<=` filters only if threshold isn't set,
 * and fail `=` filters since they have no value to match.
 *
 * Most useful late-stage: "show only pairs rated good (4) or above".
 */
export function passesRatingFilter(
  rating: PairingRating | undefined,
  filter: RatingFilterState,
): boolean {
  if (filter.threshold === null) return true
  if (rating === undefined) return false
  switch (filter.operator) {
    case '>=': return rating >= filter.threshold
    case '=': return rating === filter.threshold
    case '<=': return rating <= filter.threshold
  }
}

type RatingFilterProps = {
  state: RatingFilterState
  onChange: (next: RatingFilterState) => void
}

/**
 * Filter chip: operator (cycles ≥ / = / ≤) + a single dropdown for the
 * threshold. Empty selection = no filter. Operator chip is muted when
 * threshold is empty.
 */
export function RatingFilter({ state, onChange }: RatingFilterProps) {
  const cycleOperator = () => {
    const next: RatingFilterOperator =
      state.operator === '>=' ? '='
      : state.operator === '=' ? '<='
      : '>='
    onChange({ ...state, operator: next })
  }

  const opLabel =
    state.operator === '>=' ? '≥'
    : state.operator === '<=' ? '≤'
    : '='

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
        rating
      </span>
      <button
        onClick={cycleOperator}
        title={`Operator: ${opLabel}. Click to cycle ≥ / = / ≤`}
        disabled={state.threshold === null}
        className={`px-1.5 py-0.5 text-[11px] rounded border transition-colors font-medium ${
          state.threshold !== null
            ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
            : 'bg-white text-gray-300 border-gray-200 cursor-not-allowed'
        }`}
      >
        {opLabel}
      </button>
      <select
        value={state.threshold ?? ''}
        onChange={e => {
          const raw = e.target.value
          if (raw === '') onChange({ ...state, threshold: null })
          else onChange({ ...state, threshold: parseInt(raw, 10) as PairingRating })
        }}
        className={`px-1.5 py-0.5 text-[11px] rounded border bg-white transition-colors ${
          state.threshold !== null
            ? 'border-amber-300 text-amber-800'
            : 'border-gray-200 text-gray-500'
        }`}
      >
        <option value="">any</option>
        {([1, 2, 3, 4, 5] as PairingRating[]).map(value => (
          <option key={value} value={value}>
            {value} · {PAIRING_RATING_LABELS[value]}
          </option>
        ))}
      </select>
    </div>
  )
}
